import { Dispatch } from 'react';
import { useRouter } from 'next/router';
import Bugsnag from '@bugsnag/js';

import { IOrder, IPartialOrder } from 'checkout/types/Order';
import { actions } from 'checkout/state/checkoutActions';
import { BOX_CONDITIONS, SHOE_CONDITIONS } from 'shared/enums/ConditionsEnums';
import { requestService } from 'shared/utils/services/requestService';
import {
  IAction,
  IErrorAction,
  IOrderSuccessAction,
  IPartialOrderSuccessAction,
} from 'checkout/types/ActionPayload';
import { FeatureFlags } from 'featureFlags/constants';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { PaymentType } from 'shared/enums/PaymentType';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { t } from 'localization/utils/t';
import { useLocation } from 'shared/hooks/useLocation';
import { updateRegionPreferences } from 'account/services/regionPreferences';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';

const TP = 'checkout.hooks.useOrder';

interface IUseOrderProps {
  dispatch: Dispatch<IAction | IOrderSuccessAction | IPartialOrderSuccessAction | IErrorAction>;
  order?: IPartialOrder;
}

interface IUpdateOrderProps {
  orderNumber: number;
  payload: {
    addressId?: number;
    billingInfoId?: number;
  };
}

export interface IPurchaseOrderProps {
  finalPriceCents?: number;
  expressPayOrderNumber?: number;
  paymentType: PaymentType;
}

export const useOrder = ({ dispatch, order: orderFromState }: IUseOrderProps) => {
  const router = useRouter();
  const isCheckoutOffersEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_CHECKOUT_OFFERS);
  const { user } = useStoreSelector((state) => state.auth);
  const { selectedCountryCode } = useLocation();
  const orderLog = {
    stateOrderNumber: orderFromState?.number,
    productId: orderFromState?.product?.id,
    productTemplateId: orderFromState?.product?.productTemplate?.id,
    slug: orderFromState?.product?.slug,
    shoeCondition: orderFromState?.product?.shoeCondition,
    size: orderFromState?.product?.size,
    paymentType: orderFromState?.paymentType,
    wantId: orderFromState?.wantId || null,
    isGuest: user?.isGuest,
  };

  const fetchOrder = async (orderNumberToFetch?: number) => {
    if (!orderNumberToFetch) return;

    try {
      dispatch({ type: actions.fetchOrderRequest });

      const order: IOrder = await requestService({
        url: `/web-api/v1/orders/${orderNumberToFetch}`,
        method: 'GET',
      });

      dispatch({ type: actions.fetchOrderSuccess, payload: order });
    } catch (error) {
      dispatch({ type: actions.fetchOrderFailure, payload: error?.message });

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber: orderNumberToFetch, ...orderLog });
      });

      throw error;
    }
  };

  const purchaseOrder = async ({
    finalPriceCents,
    expressPayOrderNumber,
    paymentType,
  }: IPurchaseOrderProps) => {
    const orderNumberToConnect = orderFromState?.number || expressPayOrderNumber;

    if (!orderNumberToConnect || Number.isNaN(orderFromState?.localizedFinalPriceCents?.amount)) {
      const error = new Error('order number or final price does not exist.');

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber: orderNumberToConnect, ...orderLog });
      });

      throw error;
    }

    const isOffer = orderFromState?.wantId && isCheckoutOffersEnabled;

    try {
      dispatch({ type: actions.purchaseOrderRequest });

      // Get customer status (new or existing)
      const { returningCustomer = false }: { returningCustomer: boolean } = await requestService({
        url: '/web-api/v1/users/impact_returning_customer',
        method: 'get',
      });

      // update user locale with region preference api
      await updateRegionPreferences(selectedCountryCode);

      if (isOffer) {
        await requestService({
          url: `/web-api/v1/wants/${orderFromState?.wantId}/activate`,
          method: 'post',
        });
        sendTrackingEvent('OFFER_CREATED', {
          product_template_slug: orderFromState?.product?.productTemplate?.id,
          product_id: null,
          size: orderFromState?.product?.size,
          offer_duration: 1000,
          offer_amount: orderFromState?.priceCents / 100,
          shoe_condition: SHOE_CONDITIONS.NEW,
          box_condition: BOX_CONDITIONS.GOOD_CONDITION,
          want_id: orderFromState?.wantId,
          order_number: orderFromState?.number,
        });
      } else {
        await requestService({
          url: `/web-api/v1/orders/${orderNumberToConnect}/buy/${paymentType}`,
          method: 'put',
          ...(finalPriceCents && { data: { finalPriceCents } }),
        });
      }

      if (orderFromState?.product?.giftCardId) {
        void requestService({
          url: `/web-api/v1/orders/${orderNumberToConnect}/loki/gift_card_purchase`,
        });
      }

      if (orderFromState?.giftCards?.length) {
        void requestService({
          url: `/web-api/v1/orders/${orderNumberToConnect}/loki/gift_card_redemption`,
        });
      }

      if (orderFromState?.creditCents) {
        void requestService({
          url: `/web-api/v1/orders/${orderNumberToConnect}/loki/goat_credit`,
        });
      }

      if (user?.isGuest) {
        void requestService({
          url: `/web-api/v1/orders/${orderNumberToConnect}/loki/guest_order`,
        });
      }

      const isReturningCusomter = returningCustomer?.toString() || 'false';

      if (isReturningCusomter === 'true') {
        void router.push(
          getRedirectionPath(
            `/checkout/${orderNumberToConnect}/success?returningCustomer=${isReturningCusomter}`,
            router?.locale,
          ),
        );
      } else {
        void router.push(
          getRedirectionPath(`/checkout/${orderNumberToConnect}/success`, router?.locale),
        );
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch({
          type: actions.sessionTimeoutFailure,
          payload: t(`${TP}.sessionExpired`, 'Your session expired.'),
        });

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber: orderNumberToConnect });
        });

        throw error;
      }

      // NOTE: refresh order details if delivery date has changed after 2PM ET
      if (error?.error_code === 'delivery_dates_update') {
        void fetchOrder(orderNumberToConnect);
      }

      let message = 'Something went wrong. Please try again or reach out to support@goat.com';

      if (error?.message && error.message !== 'undefined') {
        message = error.message;
      }

      if (isOffer && error.message === 'undefined') {
        message = 'An unknown error has occurred. Please try again.';
      }

      dispatch({ type: actions.purchaseOrderFailure, payload: message });

      const _error = new Error(message);

      Bugsnag.notify(_error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber: orderNumberToConnect, ...orderLog });
      });

      throw _error;
    }
  };

  const updateOrder = async ({ orderNumber, payload }: IUpdateOrderProps) => {
    dispatch({ type: actions.updateOrderRequest });

    try {
      const response = await requestService({
        url: `/web-api/v1/orders/${orderNumber}`,
        method: 'PUT',
        data: JSON.stringify({
          order: payload,
        }),
      });

      dispatch({
        type: actions.updateOrderSuccess,
        payload: response,
      });
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch({
          type: actions.sessionTimeoutFailure,
          payload: t(`${TP}.sessionExpired`, 'Your session expired.'),
        });

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber, ...orderLog });
        });

        throw error;
      }

      dispatch({
        type: actions.updateOrderFailure,
        payload: error.message,
      });

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber, ...orderLog });
      });

      throw error;
    }
  };

  const updateAlipayOrder = async (orderNumber: number, retry = false) => {
    dispatch({ type: actions.updateOrderRequest });

    if (!orderNumber) {
      const error = new Error('Missing order number. Can not update alipay order.');

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber: null, ...orderLog });
      });

      throw error;
    }

    try {
      const response = await requestService({
        url: `/web-api/v1/orders/${orderNumber}/alipay${retry ? '?retry=true' : ''}`,
        method: 'PUT',
      });

      dispatch({
        type: actions.updateOrderSuccess,
        payload: response,
      });
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch({
          type: actions.sessionTimeoutFailure,
          payload: t(`${TP}.sessionExpired`, 'Your session expired.'),
        });

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber, ...orderLog });
        });

        throw error;
      }

      dispatch({
        type: actions.updateOrderFailure,
        payload: error.message,
      });

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber, ...orderLog });
      });

      throw error;
    }
  };

  const updateGiropayOrder = async (orderNumber: number, retry = false) => {
    dispatch({ type: actions.updateOrderRequest });

    if (!orderNumber) {
      const error = new Error('Missing order number. Can not update giropay order.');

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber: null, ...orderLog });
      });

      throw error;
    }

    try {
      const response = await requestService({
        url: `/web-api/v1/orders/${orderNumber}/giropay${retry ? '?retry=true' : ''}`,
        method: 'PUT',
      });

      dispatch({
        type: actions.updateOrderSuccess,
        payload: response,
      });
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch({
          type: actions.sessionTimeoutFailure,
          payload: t(`${TP}.sessionExpired`, 'Your session expired.'),
        });

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber, ...orderLog });
        });

        throw error;
      }

      dispatch({
        type: actions.updateOrderFailure,
        payload: error.message,
      });

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber, ...orderLog });
      });

      throw error;
    }
  };

  return {
    fetchOrder,
    purchaseOrder,
    updateOrder,
    updateAlipayOrder,
    updateGiropayOrder,
  };
};
